import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

export interface DeviceDataType {
  id: number;
  name: string;
  type: string;
  serialNumber: string;
  status: 'active' | 'inactive' | 'maintenance';
  assignedTo: string;
  location: string;
  lastCheck: string;
  ipAddress: string;
  manufacturer: string;
  model: string;
  purchaseDate: string;
  warrantyExpiry: string;
}

interface DummyJsonProduct {
  id: number;
  title: string;
  brand: string;
  category: string;
}

interface DummyJsonResponse {
  products: DummyJsonProduct[];
}

interface DeviceState {
  data: DeviceDataType[];
  loading: boolean;
  error: string | null;
}

const initialState: DeviceState = {
  data: [],
  loading: false,
  error: null
};

// ✅ Real API endpoint
const API_BASE_URL = 'https://dummyjson.com';

export const fetchDevices = createAsyncThunk<DeviceDataType[]>(
  'devices/fetchDevices',
  async () => {
    try {
      const response = await axios.get<DummyJsonResponse>(
        `${API_BASE_URL}/products?limit=10`
      );
      const products = response.data.products;

      const devices: DeviceDataType[] = products.map((product, index) => ({
        id: product.id,
        name: product.title,
        type: product.category,
        serialNumber: `SN-${product.id}`,
        status:
          index % 3 === 0
            ? 'active'
            : index % 3 === 1
            ? 'inactive'
            : 'maintenance',
        assignedTo: `User ${index + 1}`,
        location: `Room ${index + 1}`,
        lastCheck: '2025-08-01',
        ipAddress: `192.168.0.${100 + index}`,
        manufacturer: product.brand,
        model: product.title,
        purchaseDate: '2024-01-01',
        warrantyExpiry: '2026-01-01'
      }));

      return devices;
    } catch (error) {
      throw new Error('Failed to fetch devices from API');
    }
  }
);

export const addDevice = createAsyncThunk<
  DeviceDataType,
  Omit<DeviceDataType, 'id'>
>('devices/addDevice', async (newDevice, { getState }) => {
  const state = getState() as { devices: DeviceState };
  const maxId = Math.max(...state.devices.data.map(d => d.id), 0);
  const deviceWithId: DeviceDataType = {
    ...newDevice,
    id: maxId + 1
  };

  await new Promise(resolve => setTimeout(resolve, 300));
  return deviceWithId;
});

export const editDevice = createAsyncThunk<DeviceDataType, DeviceDataType>(
  'devices/editDevice',
  async updatedDevice => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return updatedDevice;
  }
);

export const deleteDevice = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('devices/deleteDevice', async (deviceId, { rejectWithValue }) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 300));
    return deviceId;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to delete device'
      );
    } else if (err instanceof Error) {
      return rejectWithValue(err.message);
    }
    return rejectWithValue('An unknown error occurred');
  }
});

const deviceSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Fetch Devices
      .addCase(fetchDevices.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDevices.fulfilled,
        (state, action: PayloadAction<DeviceDataType[]>) => {
          state.loading = false;
          state.data = action.payload;
        }
      )
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Error fetching devices';
      })

      // Add Device
      .addCase(
        addDevice.fulfilled,
        (state, action: PayloadAction<DeviceDataType>) => {
          state.data.push(action.payload);
        }
      )

      // Edit Device
      .addCase(
        editDevice.fulfilled,
        (state, action: PayloadAction<DeviceDataType>) => {
          const index = state.data.findIndex(d => d.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      )

      // Delete Device
      .addCase(
        deleteDevice.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.data = state.data.filter(
            device => device.id !== action.payload
          );
        }
      )
      .addCase(deleteDevice.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete device';
      });
  }
});

export default deviceSlice.reducer;
